// TodoPageApi.js

import axios from "axios";
import { ERROR_CODES } from '../constants/errors'; // Import error codes

const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// 1) CREATE TASK
export const createTask = async (token, params) => {
  const paramsCreateTask = {
    "influencerId": params.influencerId,
    "status": params.status,
    "taskType": params.type,
    "taskName": params.nameMenu,
    "taskPrompt": params.prompt,
    "text": params.text,
    "dateToDo": params.date,
    "completed": params.completed,
  }
  const url = `${baseUrl}/todo/api/create`;
  try {
    const response = await axios.post(url, paramsCreateTask, {
      headers: { 
        Authorization: `Bearer ${token}`, 
        "Content-Type": "application/json" 
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 201) {
      return response.data;
    } else {
      return { 
        error: response.data.error || `HTTP_ERROR_${response.status}`, 
        message: response.data.message || "An unexpected error occurred.", 
        data: response.data.data  
      };
    }
  } catch (error) {
    console.error("[apiCreateTask] Network or request error:", error);
    if (error.response) {
      return { 
        error: `HTTP_ERROR_${error.response.status}`, 
        message: error.response.data.message || "API request failed." 
      };
    } else if (error.request) {
      return { 
        error: ERROR_CODES.NETWORK_ERROR, 
        message: error.message || "No response received from server. Check network connection."
      };
    } else {
      return { 
        error: ERROR_CODES.REQUEST_ERROR, 
        message: error.message || "An unexpected error occurred while setting up the request."
      };
    }
  }
};

// 2) UPDATE TASK
export const updateTask = async (token, todoId, params) => {
  const url = `${baseUrl}/todo/api/update/${todoId}`;
  try {
    const response = await axios.patch(url, params, {
      headers: { 
        Authorization: `Bearer ${token}`, 
        "Content-Type": "application/json" 
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return { 
        error: response.data.error || `HTTP_ERROR_${response.status}`, 
        message: response.data.message || "An unexpected error occurred.", 
        data: response.data.data  
      };
    }
  } catch (error) {
    console.error("[apiUpdateTask] Network or request error:", error);
    if (error.response) {
      return { 
        error: `HTTP_ERROR_${error.response.status}`, 
        message: error.response.data.message || "API request failed." 
      };
    } else if (error.request) {
      return { 
        error: ERROR_CODES.NETWORK_ERROR, 
        message: error.message || "No response received from server. Check network connection."
      };
    } else {
      return { 
        error: ERROR_CODES.REQUEST_ERROR, 
        message: error.message || "An unexpected error occurred while setting up the request."
      };
    }
  }
};

// 3) DELETE TASK
export const deleteTask = async (token, todoId) => {
  const url = `${baseUrl}/todo/api/delete/${todoId}`;
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      return { 
        error: response.data.error || `HTTP_ERROR_${response.status}`, 
        message: response.data.message || "An unexpected error occurred.", 
        data: response.data.data  
      };
    }
  } catch (error) {
    console.error("[apiDeleteTask] Network or request error:", error);
    if (error.response) {
      return { 
        error: `HTTP_ERROR_${error.response.status}`, 
        message: error.response.data.message || "API request failed." 
      };
    } else if (error.request) {
      return { 
        error: ERROR_CODES.NETWORK_ERROR, 
        message: error.message || "No response received from server. Check network connection."
      };
    } else {
      return { 
        error: ERROR_CODES.REQUEST_ERROR, 
        message: error.message || "An unexpected error occurred while setting up the request."
      };
    }
  }
};

// 4) FETCH PENDING EMAIL TASK
export const fetchPendingEmailTask = async (token, influencerId) => {
  const url = `${baseUrl}/todo/api/pendingEmail?influencerId=${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    // Assuming a 200 status means a valid task and 204 means no task found.
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 204) {
      return null;
    } else {
      return { 
        error: response.data.error || `HTTP_ERROR_${response.status}`, 
        message: response.data.message || "An unexpected error occurred.", 
        data: response.data.data  
      };
    }
  } catch (error) {
    console.error("[apiFetchPendingEmailTask] Network or request error:", error);
    if (error.response) {
      return { 
        error: `HTTP_ERROR_${error.response.status}`, 
        message: error.response.data.message || "API request failed." 
      };
    } else if (error.request) {
      return { 
        error: ERROR_CODES.NETWORK_ERROR, 
        message: error.message || "No response received from server. Check network connection."
      };
    } else {
      return { 
        error: ERROR_CODES.REQUEST_ERROR, 
        message: error.message || "An unexpected error occurred while setting up the request."
      };
    }
  }
};

// 5) FETCH all Tasks
export const fetchTasks = async (token, filters = {}) => { 
  const url = `${baseUrl}/todo/api/list`; // Correct endpoint based on backend refactor
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: filters, // Axios handles query parameter encoding
      validateStatus: (status) => status >= 200 && status < 500, 
    };

    // Log the request being made (optional but helpful for debugging)
    console.debug(`[apiFetchTasks] Making GET request to ${url} with filters:`, filters);

    const response = await axios.get(url, config);

    if (response.status === 200) {
      // Backend should already return dates as 'YYYY-MM-DD' strings in response.data
      console.debug(`[apiFetchTasks] Received ${response.data?.length ?? 0} tasks successfully.`);
      return response.data; // Return the array of tasks
    }
    else if (response.status === 204) {
      console.debug(`[apiFetchTasks] Received 204 No Content.`);
      return []; 
    }
    else {
      console.error(`[apiFetchTasks] API Error: Status ${response.status}`, response.data);
      return {
        error: response.data?.error || `HTTP_ERROR_${response.status}`, // Use backend error code if provided
        message: response.data?.message || `API request failed with status ${response.status}.`,
        data: response.data?.data // Include extra data if the backend provides it in the error response
      };
    }
  } catch (error) {
    console.error("[apiFetchTasks] Network or request setup error:", error);
    if (axios.isAxiosError(error)) {
      // Server responded with a status code outside the validateStatus range (e.g., >= 500 if range is < 500)
      if (error.response) {
         // This case might be less common if validateStatus includes 5xx, but good practice to keep
        return {
          error: `HTTP_ERROR_${error.response.status}`,
          message: error.response.data?.message || `API request failed unexpectedly (Status: ${error.response.status}).`
        };
      }
      // The request was made but no response was received (Network Error)
      else if (error.request) {
        return {
          error: ERROR_CODES.NETWORK_ERROR,
          message: error.message || "No response received from server. Check network connection."
        };
      }
    }
    // Other errors (e.g., coding error before await, request config issue)
    return {
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message || "An unexpected error occurred while setting up the request."
    };
  }
};

export const fetchInfluencerQueryResults = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/fetchQueryResults/${influencerId}`;

  // Basic check for valid influencerId input
  if (!influencerId) {
    console.error("[fetchInfluencerQueryResults] Invalid influencerId provided:", influencerId);
    return {
        error: ERROR_CODES.REQUEST_ERROR,
        message: "Invalid influencer ID provided."
    };
  }

  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Handle HTTP status codes within the try block
      validateStatus: (status) => status >= 200 && status < 500,
    };

    console.debug(`[fetchInfluencerQueryResults] Making GET request to ${url}`);
    const response = await axios.get(url, config);

    // --- Handle Response Based on Status Code ---

    // 200 OK: Success, return the influencer data object
    if (response.status === 200) {
      // Add basic validation if possible: ensure response.data is an object and has expected keys
      if (response.data && typeof response.data === 'object' && response.data.influencerId) {
         console.debug(`[fetchInfluencerQueryResults] Received data for influencer ${influencerId}.`);
         return [response.data]; // Return the single influencer as an array for settingqueryresults
      } else {
          console.error(`[fetchInfluencerQueryResults] API returned 200 but data format is unexpected for ${influencerId}.`, response.data);
          return {
              error: ERROR_CODES.API_ERROR,
              message: "Received unexpected data format from server.",
          }
      }
    }
    // 404 Not Found: Influencer ID does not exist on backend
    else if (response.status === 404) {
        console.warn(`[fetchInfluencerQueryResults] Influencer with ID ${influencerId} not found (404).`);
        return {
            error: ERROR_CODES.NOT_FOUND,
            message: response.data?.message || `Influencer with ID ${influencerId} not found.`,
        };
    }
    // Other Statuses (Other 4xx, 5xx handled by validateStatus)
    else {
      console.error(`[fetchInfluencerQueryResults] API Error for ${influencerId}: Status ${response.status}`, response.data);
      return {
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message: response.data?.message || `API request failed with status ${response.status}.`,
        data: response.data?.data
      };
    }
  } catch (error) {
    // --- Handle Network Errors or Request Setup Errors ---
    console.error(`[fetchInfluencerQueryResults] Network or request setup error for ${influencerId}:`, error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return {
          error: `HTTP_ERROR_${error.response.status}`,
          message: error.response.data?.message || `API request failed unexpectedly (Status: ${error.response.status}).`
        };
      } else if (error.request) {
        return {
          error: ERROR_CODES.NETWORK_ERROR,
          message: "No response received from server. Check network connection."
        };
      }
    }
    return {
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message || "An unexpected error occurred while setting up the request."
    };
  }
};

// 6) RESCHEDULE TASKS
export const rescheduleTasks = async (token, todoIds, newDate) => {
  if (!todoIds || todoIds.length === 0) {
    return { error: ERROR_CODES.REQUEST_ERROR, message: "No task IDs provided for rescheduling." };
  }
  const url = `${baseUrl}/todo/api/reschedule`;
  const payload = {
    todoIds: todoIds,
    newDate: newDate, // Expected in 'YYYY-MM-DD' format
  };

  try {
    const response = await axios.patch(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data; // Success, returns { message, updatedCount }
    } else {
      // Handle 4xx, 5xx errors
      return {
        error: response.data.error || `HTTP_ERROR_${response.status}`,
        message: response.data.message || "An unexpected error occurred during rescheduling.",
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error("[apiRescheduleTasks] Network or request error:", error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || "API request failed.",
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: error.message || "No response received from server. Check network connection.",
      };
    } else {
      return {
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message || "An unexpected error occurred while setting up the request.",
      };
    }
  }
};

// NEW BATCH FETCH FUNCTION
export const fetchBatchInfluencerData = async (token, ids) => {
  const url = `${baseUrl}/user/api/batch-influencers`; // Using the new endpoint
  if (!ids || ids.length === 0) {
    return { status: true, data: [] }; // Nothing to fetch
  }
  try {
    const response = await axios.post(url, { ids }, { // Send IDs in the request body
      headers: { 
        Authorization: `Bearer ${token}`, 
        "Content-Type": "application/json" 
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { status: true, data: response.data }; // Returns array of influencer objects
    } else {
      return { 
        status: false,
        error: response.data.error || `HTTP_ERROR_${response.status}`, 
        message: response.data.message || "An unexpected error occurred.", 
      };
    }
  } catch (error) {
    console.error("[fetchBatchInfluencerData] Network or request error:", error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || "API request failed.",
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: error.message || "No response received from server. Check network connection.",
      };
    } else {
      return {
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message || "An unexpected error occurred while setting up the request.",
      };
    }
  }
};
